
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Tooltip from 'primevue/tooltip';
import ConfirmPopup from 'primevue/confirmpopup';
import { useConfirm } from 'primevue/useconfirm';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import {
  TemplateEditorState as state,
} from '@/composables/template-editor/TemplateEditor';
import { UserState } from '@/composables/User';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';

interface StockEntry {
  prizeIndex: number;
  stock: number;
}

interface PrizeInfo {
  label: string;
  index: number;
}

export default defineComponent({
  name: 'GameStockEditor',

  components: {
    InputNumber,
    Dropdown,
    Button,
    ConfirmPopup,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const property = ref<Property>(props.parserValues.properties[0]);
    const stockEntries = ref<StockEntry[]>([]);
    const prizes = ref<PrizeInfo[]>([]);
    const wonCountsMap = ref<Record<number, number>>({});

    const gameType = computed(() => props.configs.options?.gameType || 'wheel');
    let internalChange = false;
    // Last JSON written to the iframe attribute by this editor (used to ignore our own writes
    // and their echo by the field setter when observing the attribute)
    let lastWrittenJson: string | null = null;
    let attrObserver: MutationObserver | null = null;

    const isSlotMachine = computed(() => gameType.value === 'slotMachine');

    const prizeOptions = computed(() =>
      prizes.value.map((p) => ({
        label: p.label || `Prix ${p.index + 1}`,
        value: p.index,
      })),
    );

    const availablePrizes = computed(() => {
      const usedIndexes = new Set(stockEntries.value.map((e) => e.prizeIndex));
      return prizeOptions.value.filter((p) => !usedIndexes.has(p.value));
    });

    const readPrizesFromDom = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;

        const element = iframeDoc.querySelector(props.parserValues.selector);
        if (!element) return;

        const attrName = isSlotMachine.value ? 'data-win-combinations' : 'data-prizes';
        const raw = element.getAttribute(attrName);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;

        prizes.value = parsed.map((item: any, index: number) => ({
          label: isSlotMachine.value ? (item.prize || '') : (item.label || ''),
          index,
        }));

        // Read existing stock entries from DOM. The iframe attribute is the single source of truth:
        // the reload now rebuilds it with the stock saved in games_data (SC-3828), so the former
        // games_data fallback (which also resurrected a stock the user had just removed) is gone.
        const entries: StockEntry[] = [];
        parsed.forEach((item: any, index: number) => {
          if (item.stock != null && Number(item.stock) > 0) {
            entries.push({ prizeIndex: index, stock: Number(item.stock) });
          }
        });

        stockEntries.value = entries;
      } catch {
        // Ignore parsing errors
      }
    };

    const writeStockToDom = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;

        const element = iframeDoc.querySelector(props.parserValues.selector);
        if (!element) return;

        const attrName = isSlotMachine.value ? 'data-win-combinations' : 'data-prizes';
        const raw = element.getAttribute(attrName);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;

        // Build a map of prizeIndex → stock
        const stockMap = new Map<number, number>();
        stockEntries.value.forEach((entry) => {
          if (entry.stock > 0) {
            stockMap.set(entry.prizeIndex, entry.stock);
          }
        });

        // Update each prize's stock
        parsed.forEach((item: any, index: number) => {
          if (stockMap.has(index)) {
            item.stock = stockMap.get(index);
          } else {
            delete item.stock;
          }
        });

        const updatedJson = JSON.stringify(parsed);
        lastWrittenJson = updatedJson;
        element.setAttribute(attrName, updatedJson);

        // Emit change to trigger save
        internalChange = true;
        property.value.value = updatedJson;
        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
        nextTick(() => { internalChange = false; });
      } catch {
        // Ignore errors
      }
    };

    const onEntryChange = () => {
      writeStockToDom();
    };

    const addEntry = () => {
      const firstAvailable = availablePrizes.value[0];
      if (!firstAvailable) return;

      stockEntries.value.push({
        prizeIndex: firstAvailable.value,
        stock: 1,
      });
      writeStockToDom();
    };

    const removeEntry = (index: number) => {
      stockEntries.value.splice(index, 1);
      writeStockToDom();
    };

    const resetStock = (prizeIndex: number, event: Event) => {
      const wonCount = wonCountsMap.value[prizeIndex] || 0;
      if (wonCount === 0) return;

      confirm.require({
        group: 'resetStock',
        target: event.currentTarget as HTMLElement,
        message: t('templateBuilder.games.resetStockConfirm'),
        icon: 'far fa-undo',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger p-button-sm',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary p-button-sm',
        accept: async () => {
          const idShop = UserState.activeShop?.id;
          const idTemplate = state.template?.id;
          if (!idShop || !idTemplate) return;

          try {
            const result = await nestCall(
              'post',
              NestApiType.DEFAULT,
              '/template/game-reset-stock',
              {},
              '',
              { idShop, idTemplate, gameType: gameType.value, prizeIndex },
              false,
            );

            if (result?.success) {
              await fetchWonCounts();
            }
          } catch {
            // Silently ignore
          }
        },
      });
    };

    const fetchWonCounts = async () => {
      const idShop = UserState.activeShop?.id;
      const idTemplate = state.template?.id;
      if (!idShop || !idTemplate) return;

      if (stockEntries.value.length === 0) {
        wonCountsMap.value = {};
        return;
      }

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/game-won-counts',
          {},
          '',
          { idShop, idTemplate, gameType: gameType.value },
          false,
        );

        if (result?.success && Array.isArray(result.wonCounts)) {
          const map: Record<number, number> = {};
          for (const item of result.wonCounts) {
            map[item.prizeIndex] = item.wonCount;
          }
          wonCountsMap.value = map;
        }
      } catch {
        // Silently ignore
      }
    };

    // The "Prix / Lots" accordion rewrites the same attribute (labels, order, added or removed
    // prizes) from its own copy: follow those external writes so the entries and their prize
    // indexes never go stale (SC-3828).
    const stopObservingAttribute = () => {
      if (attrObserver) {
        attrObserver.disconnect();
        attrObserver = null;
      }
    };

    const startObservingAttribute = () => {
      stopObservingAttribute();
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;
        const element = iframeDoc.querySelector(props.parserValues.selector);
        if (!element) return;
        const attrName = isSlotMachine.value ? 'data-win-combinations' : 'data-prizes';
        attrObserver = new MutationObserver(() => {
          // Our own writes (and their echo by the field setter) carry the exact JSON we last wrote
          if (element.getAttribute(attrName) === lastWrittenJson) return;
          readPrizesFromDom();
        });
        attrObserver.observe(element, { attributes: true, attributeFilter: [attrName] });
      } catch (e) {
        // Iframe not available
      }
    };

    onMounted(() => {
      readPrizesFromDom();
      nextTick(() => {
        fetchWonCounts();
        startObservingAttribute();
      });
    });

    onBeforeUnmount(() => {
      stopObservingAttribute();
    });

    // Re-attach the observer when the panel values are refreshed (the iframe may have been rebuilt)
    watch(() => props.parserValues, () => {
      nextTick(() => startObservingAttribute());
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      if (!internalChange) {
        readPrizesFromDom();
      }
    }, { deep: true });

    return {
      t,
      stockEntries,
      prizeOptions,
      availablePrizes,
      wonCountsMap,
      onEntryChange,
      addEntry,
      removeEntry,
      resetStock,
    };
  },
});
